<script setup lang="ts">
import { nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'caret-change': [caretIndex: number];
  keydown: [event: KeyboardEvent];
}>();

const props = withDefaults(
  defineProps<{
    modelValue: string;
    placeholder: string;
    activeOptionId?: string | null;
    listboxId?: string;
    listboxOpen?: boolean;
    maxLength?: number;
    fillHeight?: boolean;
  }>(),
  {
    activeOptionId: null,
    listboxId: undefined,
    listboxOpen: false,
    maxLength: undefined,
    fillHeight: false,
  },
);

const MAX_HEIGHT_PX = 180;
const textareaRef = useTemplateRef('textareaRef');
// Only scroll once the height is actually capped. `scrollHeight` is an integer while the real content
// height is fractional, so a box sized to it is always a fraction short — with `overflow-y: auto`
// that shows a scrollbar the whole time.
const isCapped = ref(false);

// grows with the text up to MAX_HEIGHT_PX; past that the textarea scrolls rather than growing
// further. `fillHeight` hands the height to CSS instead, so an inline height must not linger.
const adjustHeight = (): void => {
  const element = textareaRef.value;
  if (!element) return;
  if (props.fillHeight) {
    element.style.height = '';
    return;
  }
  element.style.height = 'auto';
  // no layout to measure (the composer can mount inside a not-yet-opened <dialog>); `auto` is the
  // right height until the text changes, rather than pinning the box to 0
  if (element.scrollHeight === 0) return;
  isCapped.value = element.scrollHeight > MAX_HEIGHT_PX;
  element.style.height = `${Math.min(element.scrollHeight, MAX_HEIGHT_PX)}px`;
};

// text set from outside (edit mode, prefill) must size the box too, not just typing
onMounted(adjustHeight);
watch([() => props.modelValue, () => props.fillHeight], adjustHeight, { flush: 'post' });

const emitCaret = (): void => {
  const element = textareaRef.value;
  if (!element) return;
  emit('caret-change', element.selectionStart ?? element.value.length);
};

const handleInput = (event: Event): void => {
  const element = event.target as HTMLTextAreaElement;
  emit('update:modelValue', element.value);
  emitCaret();
  adjustHeight();
};

// the composer clips itself while it morphs open; a scroll-into-view would drag the whole content
// up inside that clip and snap it back down once the clip is released
const focus = (): void => textareaRef.value?.focus({ preventScroll: true });

const setTextAndCaret = async (text: string, caretIndex: number): Promise<void> => {
  emit('update:modelValue', text);
  await nextTick();
  const element = textareaRef.value;
  if (!element) return;
  element.focus({ preventScroll: true });
  element.setSelectionRange(caretIndex, caretIndex);
  emit('caret-change', caretIndex);
  adjustHeight();
};

defineExpose({ focus, setTextAndCaret });
</script>

<template>
  <textarea
    ref="textareaRef"
    :value="props.modelValue"
    :placeholder="props.placeholder"
    :maxlength="props.maxLength"
    rows="1"
    :role="props.listboxId ? 'combobox' : undefined"
    :aria-expanded="props.listboxId ? props.listboxOpen : undefined"
    :aria-controls="props.listboxId"
    :aria-activedescendant="props.activeOptionId ?? undefined"
    :aria-autocomplete="props.listboxId ? 'list' : undefined"
    class="block w-full resize-none border-none bg-transparent py-xs text-base text-primary caret-tint outline-none placeholder:pl-xs placeholder:text-tertiary"
    :class="[props.fillHeight ? 'h-full' : '', props.fillHeight || isCapped ? 'overflow-y-auto' : 'overflow-hidden']"
    @input="handleInput"
    @click="emitCaret"
    @keyup.left="emitCaret"
    @keyup.right="emitCaret"
    @keyup.home="emitCaret"
    @keyup.end="emitCaret"
    @keydown="emit('keydown', $event)"
  />
</template>
